import { createSlice } from '@reduxjs/toolkit';

import _ from 'lodash';

import {
  requestGetSupportQueueTickets,
  requestSaveTicket,
  requestSupportQueueStatusCount,
  requestTicketFormData,
  normalizeTicketFormData,
  getSupportDataType,
  isEmptyTicketResponse,
} from 'services/api/support';

const initialState = {
  data: { data: [] },
  count: {},
  formData: {
    tags: [],
    taskTypes: [],
    predefined: [],
  },
  fetching: false,
  save: {
    data: {},
    processing: false,
  },
  error: null,
};

const support = createSlice({
  name: 'support',
  initialState,
  reducers: {
    initSupport: (state) => {
      state.fetching = true;
      state.error = null;
    },
    initSaving: (state) => {
      state.save.processing = true;
      state.error = null;
    },
    SupportSuccess: (state, { payload }) => {
      state.data = payload;
      state.fetching = false;
    },
    NewTicketSuccess: (state, { payload }) => {
      state.save.data = payload;
      state.save.processing = false;
      state.fetching = false;
    },
    CountSuccess: (state, { payload }) => {
      state.count = payload;
    },
    FormDataSuccess: (state, { payload }) => {
      state.formData = payload;
    },
    SupportError: (state, { payload }) => {
      state.error = payload;
      state.fetching = false;
    },
    NewTicketError: (state, { payload }) => {
      state.error = payload;
      state.save.processing = false;
    },
    ResetSavedData: (state) => {
      state.save.data = {};
    },
    ClearError: (state) => {
      state.error = null;
    },
  },
});

export const {
  initSupport,
  initSaving,
  SupportSuccess,
  NewTicketSuccess,
  CountSuccess,
  SupportError,
  NewTicketError,
  FormDataSuccess,
  ResetSavedData,
  ClearError,
} = support.actions;

export const getTickets = (params) => async (dispatch, getState) => {
  dispatch(initSupport());

  const dataType = getSupportDataType(getState().user?.data?.admin_role);
  const response = await requestGetSupportQueueTickets({
    ...params,
    dataType,
  });

  const { message, data } = response ?? {};

  // The admin ticket queue returns no `success` flag, unlike the
  // `admin/tickets/*` endpoints, so the payload itself is the success signal.
  const rows = _.isArray(data) ? data : data?.data;

  if (_.isArray(rows)) {
    // Keep the paginator meta (`total`, `per_page`) alongside the rows so the
    // table can paginate server side.
    dispatch(
      SupportSuccess({ ...(_.isPlainObject(data) ? data : {}), data: rows })
    );
    return;
  }

  // No match for the applied filters is an empty table, not a failure.
  if (isEmptyTicketResponse(response)) {
    dispatch(SupportSuccess({ data: [], total: 0 }));
    return;
  }

  dispatch(SupportError(message ?? 'Unable to load support tickets'));
};

export const saveTicket =
  (params, onFailure = () => {}) =>
  async (dispatch) => {
    dispatch(initSaving());
    const { success, message, data } = await requestSaveTicket(params);
    if (success) {
      dispatch(NewTicketSuccess(data));
    } else {
      const error = message ?? 'An error occured';
      dispatch(NewTicketError(error));
      onFailure(error, success);
    }
  };

export const getTicketCount = () => async (dispatch, getState) => {
  dispatch(initSupport());

  const dataType = getSupportDataType(getState().user?.data?.admin_role);
  const { success, message, data } = await requestSupportQueueStatusCount(
    dataType
  );

  if (success) {
    dispatch(CountSuccess(_.isArray(data) ? _.first(data) ?? {} : data));
  } else {
    dispatch(SupportError(message));
  }
};

export const getTicketFormData = () => async (dispatch) => {
  const response = await requestTicketFormData();

  if (response?.success === false) {
    dispatch(SupportError(response?.message));
    return;
  }

  dispatch(
    FormDataSuccess(normalizeTicketFormData(response?.data ?? response))
  );
};

export const resetSavedData = () => async (dispatch) => {
  dispatch(ResetSavedData());
};

export default support.reducer;
